import { useEffect, useRef, useState, useMemo, useCallback } from 'react'
import { getTimeFromSeconds } from '@/utils/index'
import { useRecoilState } from 'recoil'
import analytics from '@/utils/analytics'
import {
  seekToState,
  videoProgressState,
  isPremiumState,
  modalState,
} from '@/store'
import {
  PlusCircle,
  AlertCircle,
  Zap,
  ZapOff,
  ChevronsDown,
  Copy,
  MoreHorizontal,
  Download,
} from 'react-feather'
import toast from 'react-hot-toast'
import { useRouter } from 'next/router'
import Button from '@/components/Button'
import ReactTooltip from 'react-tooltip'
import { useShowTranscriptTabModal } from '@/components/Modal'
import Dropdown from '@/components/Dropdown'
import useLoadTranscript from '@/utils/useLoadTranscript'
import { getYouTubeID } from '@/utils/index'
import {
  PDFDownloadLink,
  Document,
  Page,
  Text,
  View,
  StyleSheet,
} from '@react-pdf/renderer'
import Spinner from '@/components/Spinner'
import Input from '@/components/Input'
import classNames from 'classnames'
import useDebounce from '@/utils/useDebounce'

export default function TranscriptTab({
  videoUrl,
  duration,
  currentView,
  setCurrentView,
  setTitle,
  setNewContent,
  content,
  onCreate,
  isDemo,
  videoTitle,
}) {
  const [searchQuery, setSearchQuery] = useState('')
  const [isAutoScroll, setIsAutoScroll] = useState(false)
  const [shouldRender, setShouldRender] = useState(false)
  const [, setSeekTo] = useRecoilState(seekToState)
  const [progress] = useRecoilState(videoProgressState)
  const [isPremium] = useRecoilState(isPremiumState)
  const showNeedToUpgradeModal = useShowTranscriptTabModal()
  const currentTranscript = useRef()
  const router = useRouter()
  const debouncedSearchQuery = useDebounce(searchQuery, 300)
  const ytVideoId = useMemo(() => getYouTubeID(videoUrl), [videoUrl])
  const { isLoading, transcript } = useLoadTranscript({ ytVideoId, isDemo })

  useEffect(function getTranscriptAutoScrollDefaultValue() {
    if (localStorage) {
      const savedValue = localStorage.getItem('transcriptAutoScroll')
      const value = savedValue === 'true'
      setIsAutoScroll(value)
    }
  }, [])

  useEffect(
    function autoHighlighTranscript() {
      if (!progress || !transcript) {
        return
      }

      // Remove previous highlight
      if (currentTranscript && currentTranscript.current) {
        const currentTranscriptEl = document.querySelector(
          `#${getIdFromStart(currentTranscript.current.start)}`
        )
        if (currentTranscriptEl) {
          currentTranscriptEl.style.background = null
          currentTranscriptEl.style.color = null
        }
      }

      // Add new highlight
      const currentItem = getCurrentTranscriptItem({
        transcript,
        progress,
      })

      currentTranscript.current = currentItem

      if (currentItem) {
        const el = document.querySelector(
          `#${getIdFromStart(currentItem.start)}`
        )

        if (!el) {
          return
        }

        if (isAutoScroll) {
          document.getElementById('transcript-container').scrollTop =
            el.offsetTop - 50
        }

        el.style.background = '#6381f3'
        el.style.color = 'white'
      }
    },
    [progress, transcript, isAutoScroll]
  )

  useEffect(
    function setLoading() {
      setTimeout(() => {
        setShouldRender(currentView === 'transcript')
      }, 300)
    },
    [currentView]
  )

  function selectTranscript(start) {
    analytics.track('Seek to transcript')
    setSeekTo({ time: +start, shouldPlay: true })
  }

  const userCanLoadTranscript = isPremium || isDemo
  const showTranscript = userCanLoadTranscript
    ? !isLoading && shouldRender
    : true

  const hasTranscript = showTranscript && transcript && transcript.length > 0

  const transcriptByRange = useMemo(() => {
    if (!transcript) {
      return {}
    }

    const range = 30
    const transcriptByRange = transcript.reduce((acc, item) => {
      if (
        debouncedSearchQuery &&
        !item.text.toLowerCase().includes(debouncedSearchQuery.toLowerCase())
      ) {
        return acc
      }

      const floor = Math.floor(+item.start / range)
      const rangeKey = floor * range
      return {
        ...acc,
        [rangeKey]: [...(acc[rangeKey] || []), item],
      }
    }, {})

    return transcriptByRange
  }, [transcript, debouncedSearchQuery])

  useEffect(() => {
    if (!userCanLoadTranscript) {
      showNeedToUpgradeModal(router)
    }
  }, [userCanLoadTranscript, showNeedToUpgradeModal, router])

  return (
    <div
      className="relative overflow-x-auto overflow-y-hidden bg-white p-3 h-full"
      style={{
        border: '1px solid #e0e0e0',
        // 0.75rem because p-3
        paddingBottom: userCanLoadTranscript ? '100px' : '0.75rem',
      }}
    >
      {showTranscript && userCanLoadTranscript && hasTranscript && (
        <TranscriptMenu
          {...{
            progress,
            isAutoScroll,
            setIsAutoScroll,
            transcript,
            setCurrentView,
            setNewContent,
            content,
            onCreate,
            setTitle,
            duration,
            videoTitle,
            transcriptByRange,
            setSearchQuery,
          }}
        />
      )}

      <div
        id="transcript-container"
        className="relative overflow-y-auto h-full pt-2 scroll-mask"
      >
        {!showTranscript && (
          <div className="h-full center">
            <Spinner />
          </div>
        )}

        {showTranscript &&
          transcript &&
          Object.keys(transcriptByRange).map((range) => (
            <div
              key={range}
              className="text-lg mb-2 border border-gray-200 py-2 px-4 rounded-md"
            >
              <span
                className="py-1 px-2 mr-2 bg-gray-300 text-gray-700 rounded-md no-select clickable-opacity"
                onClick={() => selectTranscript(range)}
              >
                {getTimeFromSeconds(range)}
              </span>
              <span>
                {transcriptByRange[range].map(({ text, start }) => (
                  <span
                    key={start}
                    id={getIdFromStart(start)}
                    className="cursor-pointer text-gray-800 hover:bg-blue-400 hover:text-white"
                    onClick={() => selectTranscript(start)}
                  >
                    {text}{' '}
                  </span>
                ))}
              </span>
            </div>
          ))}

        {!userCanLoadTranscript && (
          <div className="center h-full flex-col">
            <AlertCircle className="mb-3" />
            Viewing the transcript is a premium feature
            <Button
              className="mt-3"
              onClick={() => {
                analytics.track('(Transcript Tab) Not Premium')
                showNeedToUpgradeModal(router)
              }}
            >
              Learn More
            </Button>
          </div>
        )}

        {!hasTranscript && !isLoading && userCanLoadTranscript && (
          <div className="center h-full">
            <AlertCircle className="mr-3" />
            Seems that this video doesn&apos;t have any transcript available.
          </div>
        )}
      </div>
    </div>
  )
}

function TranscriptMenu({
  progress,
  isAutoScroll,
  setIsAutoScroll,
  transcript,
  setCurrentView,
  setNewContent,
  content,
  onCreate,
  setTitle,
  duration,
  videoTitle,
  transcriptByRange,
  setSearchQuery,
}) {
  const [isFirstRender, setIsFirstRender] = useState(true)
  const [newStart, setNewStart] = useState(null)
  const [selectedText, setSelectedText] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const [, setModal] = useRecoilState(modalState)

  const scrollToCurrentTime = useCallback(() => {
    if (!progress) {
      return
    }

    const currentItem = getCurrentTranscriptItem({
      transcript,
      progress,
    })

    if (!currentItem) {
      return
    }

    const el = document.getElementById(getIdFromStart(currentItem.start))
    document.getElementById('transcript-container').scrollTop =
      el.offsetTop - 50

    el.style.background = '#6381f3'
    el.style.color = 'white'

    setTimeout(() => {
      el.style.background = null
      el.style.color = null
    }, 2000)
  }, [progress, transcript])

  const removeTimeFromText = useCallback(
    (text) => {
      if (!text) {
        return null
      }

      const hasHours = duration > 60 * 60
      const regex = hasHours
        ? /([0-9][0-9]):([0-9][0-9]):([0-9][0-9])/g
        : /([0-9][0-9]):([0-9][0-9])/g

      return text.replace(regex, '').replace(/\n/g, ' ')
    },
    [duration]
  )

  useEffect(
    function mouseUpListener() {
      function onPointerUp() {
        setTimeout(() => {
          const text = window.getSelection().toString()

          // Reset state
          if (selectedText && !text) {
            setSelectedText('')
            return
          }

          const textWithoutTime = removeTimeFromText(text)
          if (textWithoutTime) {
            const firstSelectedElement = getSelectionBoundaryElement()
            setNewStart(getStartFromId(firstSelectedElement.id))
            setSelectedText(textWithoutTime)
          }
        }, 0)
      }

      window.addEventListener('pointerup', onPointerUp)

      return () => {
        window.removeEventListener('pointerup', onPointerUp)
      }
    },
    [selectedText, transcript, removeTimeFromText]
  )

  useEffect(() => {
    if (isFirstRender) {
      scrollToCurrentTime()
      setIsFirstRender(false)
    }
  }, [scrollToCurrentTime, isFirstRender])

  async function createNewAnnotation() {
    if (!selectedText) {
      return
    }

    setIsCreating(true)

    setTitle('')
    const text = `> ${selectedText}`
    setNewContent({ text, start: newStart })
    try {
      await onCreate(true, text, newStart)
    } catch (error) {
      toast.error('An error ocurred')
    } finally {
      setIsCreating(false)
    }
  }

  function copyToEditor() {
    if (!selectedText) {
      return
    }

    const newContent = content
      ? `${content} \n\n > ${selectedText}`
      : `> ${selectedText}`
    setNewContent({ text: newContent, start: newStart })
    setCurrentView('edit')
  }

  function onAutoScrollClick() {
    const newValue = !isAutoScroll
    analytics.track('Transcript Auto scroll clicked', { value: newValue })
    setIsAutoScroll(newValue)

    if (localStorage) {
      localStorage.setItem('transcriptAutoScroll', newValue)
    }
  }

  function onOptionChange(option) {
    analytics.track('Transcript menu dropdown', { option })

    switch (option) {
      case 'scroll-to-current':
        scrollToCurrentTime()
        break

      case 'toggle-autoscroll':
        onAutoScrollClick()
        break

      case 'download':
        setModal({
          title: 'Download transcript',
          body: (
            <DownloadTranscriptModalBody
              {...{ videoTitle, transcriptByRange }}
            />
          ),
        })
        break

      default:
        break
    }
  }

  const optionClassName = classNames('flex', {
    'clickable-opacity': selectedText,
    'cursor-default': !selectedText,
  })

  return (
    <div className="flex justify-center pb-3">
      <div className="bg-gray-300 rounded-md px-3 py-2 flex shadow-md center">
        <Input
          onChange={(e) => setSearchQuery(e.target.value)}
          type="text"
          placeholder="Search"
          className="flex-1"
        />

        <Divider />

        {!selectedText && (
          <ReactTooltip
            place="top"
            effect="solid"
            id="create-new-annotations"
            clickable
            delayHide={100}
          >
            <span className="center">Highlight some text first</span>
          </ReactTooltip>
        )}
        <span
          data-tip
          data-for="create-new-annotations"
          className={`${optionClassName} ${selectedText ? '' : 'opacity-40'}`}
          onClick={createNewAnnotation}
        >
          {isCreating ? (
            <Spinner className="mr-3" size={6} />
          ) : (
            <PlusCircle className="mr-3" />
          )}
          <span className="hidden md:block">Create</span>
        </span>

        <Divider />

        {!selectedText && (
          <ReactTooltip
            place="top"
            effect="solid"
            id="copy-to-annotation"
            clickable
            delayHide={100}
          >
            <span className="center">Highlight some text first</span>
          </ReactTooltip>
        )}
        <span
          data-tip
          data-for="copy-to-annotation"
          className={`${optionClassName} ${selectedText ? '' : 'opacity-40'}`}
          onClick={copyToEditor}
        >
          <Copy className="mr-3" />
          <span className="hidden md:block">Copy</span>
        </span>

        <Divider />

        <MoreOptions
          {...{
            onOptionChange,
            isAutoScroll,
          }}
        />
      </div>
    </div>
  )
}

function MoreOptions({ onOptionChange, isAutoScroll }) {
  const download = {
    name: 'Download',
    value: 'download',
    icon: (
      <Download className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
    ),
  }

  const scrollToCurrentTime = {
    name: 'Scroll to current time',
    value: 'scroll-to-current',
    icon: (
      <ChevronsDown className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
    ),
  }

  const autoScroll = {
    name: isAutoScroll ? 'Autoscroll On' : 'Autoscroll Off',
    value: 'toggle-autoscroll',
    icon: isAutoScroll ? (
      <Zap className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
    ) : (
      <ZapOff className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
    ),
  }

  return (
    <Dropdown
      options={[download, scrollToCurrentTime, autoScroll]}
      onChange={onOptionChange}
    >
      <span className="flex cursor-pointer">
        <MoreHorizontal className="mr-3" />{' '}
        <span className="hidden md:block">More</span>
      </span>
    </Dropdown>
  )
}

function DownloadTranscriptModalBody({ videoTitle, transcriptByRange }) {
  const [showDownload, setShowDownload] = useState(false)

  useEffect(() => {
    const timeoutID = setTimeout(() => {
      setShowDownload(true)
    }, 300)

    return () => clearTimeout(timeoutID)
  }, [])

  if (!showDownload) {
    return (
      <div className="w-full center">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="w-full center">
      <PDFDownloadLink
        document={<TranscriptPdf {...{ videoTitle, transcriptByRange }} />}
        fileName={`${videoTitle}.pdf`}
      >
        {({ loading }) => (loading ? <Spinner /> : <Button>Download</Button>)}
      </PDFDownloadLink>
    </div>
  )
}

function TranscriptPdf({ videoTitle, transcriptByRange }) {
  const styles = StyleSheet.create({
    body: {
      paddingTop: 35,
      paddingBottom: 65,
      paddingHorizontal: 35,
    },
    title: {
      fontSize: 24,
      textAlign: 'center',
      marginBottom: 20,
    },
    startTime: {
      fontSize: 16,
      marginLeft: 12,
      marginTop: 15,
      fontFamily: 'Times-Roman',
    },
    text: {
      margin: 12,
      fontSize: 14,
      textAlign: 'justify',
      fontFamily: 'Times-Roman',
    },
    pageNumber: {
      position: 'absolute',
      fontSize: 12,
      bottom: 30,
      left: 0,
      right: 0,
      textAlign: 'center',
      color: 'grey',
    },
  })

  return (
    <Document>
      <Page size="A4" style={styles.body}>
        <Text style={styles.title}>{videoTitle}</Text>

        {Object.keys(transcriptByRange).map((range) => (
          <View key={range} style={styles.section} wrap={false}>
            <Text style={styles.startTime}>{getTimeFromSeconds(range)}</Text>
            <Text style={styles.text}>
              {transcriptByRange[range].map(({ text }) => `${text} `)}
            </Text>
          </View>
        ))}

        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) =>
            `${pageNumber} / ${totalPages}`
          }
          fixed
        />
      </Page>
    </Document>
  )
}

function Divider() {
  return <div className="mx-3" />
}

function getCurrentTranscriptItem({ transcript, progress }) {
  const currentProgress = progress.playedSeconds

  // Handle edge cases
  if (!transcript.length) {
    return null
  }

  if (currentProgress < +transcript[0].start) {
    return null
  }

  if (currentProgress >= +transcript[transcript.length - 1].start) {
    return transcript[transcript.length - 1]
  }

  // Binary search implementation
  let left = 0
  let right = transcript.length - 1

  while (left <= right) {
    const mid = Math.floor((left + right) / 2)
    const currentStart = +transcript[mid].start
    const nextStart =
      mid < transcript.length - 1 ? +transcript[mid + 1].start : Infinity

    if (currentStart <= currentProgress && currentProgress < nextStart) {
      return transcript[mid]
    }

    if (currentProgress < currentStart) {
      right = mid - 1
    } else {
      left = mid + 1
    }
  }

  return null
}

function getIdFromStart(start) {
  return `start-${start.replace('.', '-')}`
}

function getStartFromId(id) {
  return +id.replace('start-', '').replace('-', '.')
}

function getSelectionBoundaryElement(isStart = true) {
  let range
  let sel
  let container

  if (document.selection) {
    range = document.selection.createRange()
    range.collapse(isStart)
    return range.parentElement()
  } else {
    sel = window.getSelection()
    if (sel.getRangeAt) {
      if (sel.rangeCount > 0) {
        range = sel.getRangeAt(0)
      }
    } else {
      // Old WebKit
      range = document.createRange()
      range.setStart(sel.anchorNode, sel.anchorOffset)
      range.setEnd(sel.focusNode, sel.focusOffset)

      // Handle the case when the selection was selected backwards (from the end to the start in the document)
      if (range.collapsed !== sel.isCollapsed) {
        range.setStart(sel.focusNode, sel.focusOffset)
        range.setEnd(sel.anchorNode, sel.anchorOffset)
      }
    }

    if (range) {
      container = range[isStart ? 'startContainer' : 'endContainer']

      // Check if the container is a text node and return its parent if so
      return container.nodeType === 3 ? container.parentNode : container
    }
  }
}
